// @flow
// $FlowFixMe
import React, { useMemo, useState, Fragment } from "react";
import { get } from "lodash";
import { connect } from "react-redux";
import mediator from "@tvg/mediator";
import RaceCardSortInfo from "@fdr/atomic-ui/_molecules/RaceCardSort";
import RaceCardSortOptions from "@fdr/atomic-ui/_organisms/RaceCardSortOptions";
import { type SortTypeEnum } from "@fdr/types/ProgramPage";
import uam from "@tvg/api/uam";
import { onOpenComplianceModal } from "@tvg/sh-lib-account-wallet/src/utils/mobileUtils";
import { isAccountCompliantSelector } from "@tvg/sh-utils/sessionUtils";

import ModalV2 from "@fdr/atomic-ui/_templates/ModalV2";
import {
  type Actions,
  userSetHandicappingSortModeAction,
  setOpenAllRunners,
  setSelectedRunners,
  shouldUpdatePicks
} from "@fdr/shared-actions/ProgramPageActions";

import { setNewPreferenceValue } from "@tvg/shared-actions/UserActions";

import { type Dispatch } from "redux";
import type { UnaryFn } from "@tvg/types/Functional";

type Props = {
  sortType: SortTypeEnum,
  userData: { logged: boolean },
  dispatch: Dispatch<Actions | *>,
  isAllRunnersOpen: boolean[],
  selectedRunners: string[][],
  betLeg: number,
  isLogged: boolean,
  raceNumber: string,
  trackName: string,
  isAccountCompliant?: boolean
};

export const changeModalState = (
  isModalActive: boolean,
  setModalActive: UnaryFn<boolean, void>
) => {
  if (isModalActive) {
    mediator.base.dispatch({ type: "HANDICAPPING_SORT:CLOSE_MODAL" });
  } else {
    mediator.base.dispatch({ type: "HANDICAPPING_SORT:OPEN_MODAL" });
  }
  setModalActive(!isModalActive);
};

export const handleLoginOpenAllRunnersCallback = (
  allOpenArray: boolean[],
  dispatch: Dispatch<Actions | *>
) => {
  dispatch(setOpenAllRunners(allOpenArray));
  dispatch(shouldUpdatePicks(true));
};

export const openAllRunners = (props: Props) => {
  const {
    isAllRunnersOpen,
    betLeg,
    selectedRunners,
    isLogged,
    dispatch,
    isAccountCompliant
  } = props;

  const allOpenArray = isAllRunnersOpen.slice();
  const isOpen = isAllRunnersOpen[betLeg];
  allOpenArray[betLeg] = !isOpen;

  if (!isLogged) {
    mediator.base.dispatch({
      type: "OPEN_LOGIN",
      payload: {
        callback: () => {
          handleLoginOpenAllRunnersCallback(allOpenArray, dispatch);
        }
      }
    });
    return;
  }

  if (!isAccountCompliant) {
    onOpenComplianceModal();
    return;
  }

  if (isAllRunnersOpen[betLeg]) {
    selectedRunners[betLeg] = [];
    dispatch(setSelectedRunners(selectedRunners));
    mediator.base.dispatch({
      type: "RUNNER_INFO:EXPAND_COLLAPSE",
      payload: {
        expandAction: "Collapse",
        runnerId: "all"
      }
    });
  }
  dispatch(setOpenAllRunners(allOpenArray));

  dispatch(shouldUpdatePicks(true));
};

export const changeSortType = (
  type: SortTypeEnum,
  props: Props,
  isModalActive: boolean,
  setModalActive: UnaryFn<boolean, void>
) => {
  const { userData, dispatch, isLogged, trackName, raceNumber } = props;

  const accountNumber = +get(userData, "user.accountNumber", -1);

  if (!isLogged) {
    dispatch(userSetHandicappingSortModeAction(type));
    setModalActive(!isModalActive);
    mediator.base.dispatch({
      type: "HANDICAPPING_SORT:SELECT_DATA_TYPE",
      payload: { type, trackName, raceNumber, order: "ascending" }
    });
  } else {
    const handicappingSortFavoriteMode = {
      basic: type
    };

    setModalActive(!isModalActive);

    uam
      .setUserPref(
        accountNumber,
        `handicappingSortFavoriteMode`,
        JSON.stringify(handicappingSortFavoriteMode)
      )
      .then((data) => {
        if (data.status === 200) {
          dispatch(
            setNewPreferenceValue(
              `handicappingSortFavoriteMode`,
              JSON.stringify(handicappingSortFavoriteMode)
            )
          );
          dispatch(userSetHandicappingSortModeAction(type));
          mediator.base.dispatch({
            type: "HANDICAPPING_SORT:SELECT_DATA_TYPE",
            payload: { type, trackName, raceNumber, order: "ascending" }
          });
        }
      })
      .catch(console.error);
  }
};

export const RaceCardSort = (props: Props) => {
  const { sortType, isAllRunnersOpen, betLeg } = props;
  const [isModalActive, setModalActive] = useState(false);

  const callChangeSortType = (type: SortTypeEnum) =>
    changeSortType(type, props, isModalActive, setModalActive);

  const callChangeModalState = () =>
    changeModalState(isModalActive, setModalActive);

  return useMemo(
    () => (
      <Fragment>
        <RaceCardSortInfo
          onSortModalClick={callChangeModalState}
          sortType={sortType || "odds"}
          openAllRunners={() => openAllRunners(props)}
          isAllRunnersOpen={isAllRunnersOpen[betLeg]}
        />
        <ModalV2
          onClose={callChangeModalState}
          title="Sort by"
          isCapitalized={false}
          isOpen={isModalActive}
          isFullHeight={false}
          animation="bottom"
        >
          {() => (
            <RaceCardSortOptions
              sortType={sortType || "odds"}
              onSortTypeClick={callChangeSortType}
            />
          )}
        </ModalV2>
      </Fragment>
    ),
    [
      betLeg,
      isModalActive,
      sortType,
      isAllRunnersOpen,
      JSON.stringify(isAllRunnersOpen)
    ]
  );
};

RaceCardSort.defaultProps = {
  sortType: "odds",
  setOpenAllRunners: false,
  isAllRunnersOpen: [],
  selectedRunners: [[]],
  betLeg: 0,
  isLogged: false,
  isAccountCompliant: false,
  raceNumber: "",
  trackName: ""
};

export default connect((store) => ({
  userData: get(store, "userData"),
  betLeg: get(store, "RaceProgram.betLeg", 0),
  sortType: get(store, "RaceProgram.userSetHandicappingSortMode", "odds"),
  isAllRunnersOpen: get(store, "RaceProgram.isAllRunnersOpen", false),
  selectedRunners: get(store, "RaceProgram.selectedRunners", [[]]),
  isLogged: get(store, "userData.logged", false),
  isAccountCompliant: isAccountCompliantSelector(store),
  raceNumber: get(store, "RaceProgram.currentRace.raceNumber", ""),
  trackName: get(store, "RaceProgram.currentRace.track.trackName", "")
}))(RaceCardSort);
